import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { menuService } from '../utils/api';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for fetching courses with caching
 */
export const useCourses = (schoolId) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['courses', schoolId, language],
    queryFn: async () => {
      // Add timestamp parameter to prevent browser disk caching
      const timestamp = Date.now();
      console.log(`[CoursesDebug] Fetching courses for schoolId: ${schoolId}, language: ${language} at ${new Date(timestamp).toISOString()}`);
      return menuService.getCourses(schoolId, language, timestamp);
    },
    enabled: !!schoolId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Custom hook for fetching a single course
 */
export const useCourse = (courseId) => {
  return useQuery({
    queryKey: ['course', courseId],
    queryFn: () => menuService.getCourseTranslations(courseId),
    enabled: !!courseId,
  });
};

/**
 * Custom hook for creating a course with optimistic updates
 */
export const useCreateCourse = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useMutation({
    mutationFn: ({ schoolId, name, translations = {} }) =>
      menuService.createCourse(schoolId, name, translations),
    
    onMutate: async ({ schoolId, name, translations = {} }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['courses', schoolId, language],
      });

      // Snapshot the previous value
      const previousCourses = queryClient.getQueryData(['courses', schoolId, language]);

      // Optimistically update the cache with new course
      if (previousCourses) {
        const optimisticCourse = {
          id: `temp-${Date.now()}`,
          school_id: schoolId,
          name: name,
          created_at: new Date().toISOString(),
          // Add any other required properties
        };

        queryClient.setQueryData(
          ['courses', schoolId, language],
          [...previousCourses, optimisticCourse]
        );
      }

      return { previousCourses };
    },
    
    onError: (err, { schoolId }, context) => {
      // If the mutation fails, revert to the previous value
      if (context?.previousCourses) {
        queryClient.setQueryData(
          ['courses', schoolId, language],
          context.previousCourses
        );
      }
    },
    
    onSettled: (data, error, { schoolId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['courses', schoolId, language],
      });
      
      // Also invalidate availableCoursesForMenuType queries as the available courses have changed
      queryClient.invalidateQueries({
        queryKey: ['availableCoursesForMenuType'],
      });
    },
  });
};

/**
 * Custom hook for updating a course with optimistic updates
 */
export const useUpdateCourse = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useMutation({
    mutationFn: ({ id, ...data }) => menuService.updateCourse(id, data),
    
    onMutate: async ({ id, schoolId, name, translations }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['courses', schoolId, language],
      });
      await queryClient.cancelQueries({
        queryKey: ['course', id],
      });

      // Snapshot previous values
      const previousCourses = queryClient.getQueryData(['courses', schoolId, language]);
      const previousCourse = queryClient.getQueryData(['course', id]);

      // Optimistically update courses list
      if (previousCourses) {
        const updatedCourses = previousCourses.map(course =>
          course.id === id
            ? { 
                ...course, 
                name,
                // Update translations for the list view if available
                translations: course.translations
                  ? course.translations.map(trans => {
                      const newValue = translations ? translations[trans.code] : undefined;
                      return {
                        ...trans,
                        name: newValue !== undefined ? newValue : trans.name
                      };
                    })
                  : undefined
              }
            : course
        );

        // Update the cache with our optimistic update
        queryClient.setQueryData(['courses', schoolId, language], updatedCourses);
      }

      // Optimistically update single course
      if (previousCourse && translations) {
        const updatedTranslations = { ...previousCourse };
        // Update all translations provided
        Object.entries(translations).forEach(([code, text]) => {
          updatedTranslations[code] = text;
        });
        
        queryClient.setQueryData(['course', id], updatedTranslations);
      }

      return { previousCourses, previousCourse };
    },
    
    onError: (err, { id, schoolId }, context) => {
      // If the mutation fails, revert to the previous values
      if (context?.previousCourses) {
        queryClient.setQueryData(
          ['courses', schoolId, language],
          context.previousCourses
        );
      }
      
      if (context?.previousCourse) {
        queryClient.setQueryData(['course', id], context.previousCourse);
      }
    },
    
    onSettled: (data, error, { id, schoolId }) => {
      // Force a fresh refetch with timestamp to ensure no stale disk cache
      console.log(`[CoursesDebug] Invalidating courses cache for schoolId: ${schoolId} after course update`);
      
      // Invalidate and refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['courses', schoolId, language],
        refetchType: 'all',
      });
      
      queryClient.invalidateQueries({
        queryKey: ['course', id],
        refetchType: 'all',
      });
      
      // Also invalidate any menu types that might include this course
      queryClient.invalidateQueries({
        queryKey: ['menuTypeCourses'],
      });
      
      // Also invalidate availableCoursesForMenuType queries as the available courses have changed
      queryClient.invalidateQueries({
        queryKey: ['availableCoursesForMenuType'],
      });
      
      // Invalidate public menu queries as course names may have changed
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu',
      });
    },
  });
};

/**
 * Custom hook for deleting a course with optimistic updates
 */
export const useDeleteCourse = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useMutation({
    mutationFn: (params) => {
      // Handle both direct id or object with id
      const courseId = typeof params === 'object' ? params.id : params;
      return menuService.deleteCourse(courseId);
    },
    
    onMutate: async ({ id, schoolId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['courses', schoolId, language],
      });

      // Snapshot the previous value
      const previousCourses = queryClient.getQueryData(['courses', schoolId, language]);

      // Optimistically update the cache by removing the course
      if (previousCourses) {
        queryClient.setQueryData(
          ['courses', schoolId, language],
          previousCourses.filter(course => course.id !== id)
        );
      }

      return { previousCourses };
    },
    
    onError: (err, { id, schoolId }, context) => {
      // If the mutation fails, revert to the previous value
      if (context?.previousCourses) {
        queryClient.setQueryData(
          ['courses', schoolId, language],
          context.previousCourses
        );
      }
    },
    
    onSettled: (data, error, { id, schoolId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['courses', schoolId, language],
      });
      
      // Also remove from cache
      queryClient.removeQueries({
        queryKey: ['course', id],
      });
      
      // Also invalidate any menu types that might include this course
      queryClient.invalidateQueries({
        queryKey: ['menuTypeCourses'],
      });
      
      // Also invalidate availableCoursesForMenuType queries as the available courses have changed
      queryClient.invalidateQueries({
        queryKey: ['availableCoursesForMenuType'],
      });
      
      // Invalidate public menu queries as course may have been used in menus
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu',
      });
    },
  });
};

/**
 * Custom hook for updating a course's translations
 */
export const useUpdateCourseTranslations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, translations }) => 
      menuService.updateCourseTranslations(courseId, translations),
    
    onMutate: async ({ courseId, translations }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['course', courseId],
      });

      // Snapshot the previous value
      const previousTranslations = queryClient.getQueryData(['course', courseId]);

      // Optimistically update translations
      if (previousTranslations) {
        queryClient.setQueryData(
          ['course', courseId],
          { ...previousTranslations, ...translations }
        );
      }

      return { previousTranslations };
    },
    
    onError: (err, { courseId }, context) => {
      // If the mutation fails, revert to the previous value
      if (context?.previousTranslations) {
        queryClient.setQueryData(
          ['course', courseId],
          context.previousTranslations
        );
      }
    },
    
    onSettled: (data, error, { courseId, schoolId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['course', courseId],
      });
      
      // Also invalidate the courses list as names may have changed
      if (schoolId) {
        queryClient.invalidateQueries({
          queryKey: ['courses', schoolId],
        });
      }
      
      // Invalidate public menu queries as course names may have changed
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu',
      });
    },
  });
}; 